/* Depth is encoded in HUE, not in a tint ramp: a lightness ladder runs out of
   contrast after ~3 steps and runs out sooner in dark mode, where the same
   ramp has to go the other way. A spine colour survives both.

   The four hues used to be written here as Moonbase's seafoam, blue, violet and
   pink (#2019). They are brand tokens now — `--rel-1..4`, emitted by
   `@solum/experience` and derived by hue rotation from the brand's own colour —
   so a rebranded tenant sees their own relationships in their own palette
   instead of ours. There is no tenant branch anywhere: one expression, four
   values that arrive with the theme. */
.band {
  position: relative;
  min-width: 0;
  background: color-mix(in srgb, var(--ink) 2.5%, var(--surface));
  border-top: 1px solid var(--hair);
  border-bottom: 1px solid var(--hair);
}
.spine { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; background: var(--spine); }

/* Indent for the first two levels, then stop. Past that the spine and the trail
   carry the depth — continuing to indent squeezes the child grid exactly when it
   needs more room, which is what makes deep nesting unreadable. */
.inner { min-width: 0; padding: var(--sp-3) var(--sp-3) var(--sp-3) var(--sp-4); }
.band[data-depth='1'] .inner { padding-left: calc(var(--sp-4) + var(--sp-4-5)); }
.band[data-depth='2'] .inner { padding-left: calc(var(--sp-4) + var(--sp-4-5)); }

.title { font-size: var(--fs-sm); font-weight: 650; letter-spacing: -0.01em; }
.trail { font-size: var(--fs-xs); color: var(--muted); }

/* The child grid uses the shared DataGrid on the band's surface rather than the page's,
   with a hairline box so the reader can see where the inner grid starts and the
   outer one resumes. A nested grid drawn with no boundary is the thing that makes
   people lose which one they are reading. */
.grid {
  --row-h: 34px;
  width: 100%;
  min-width: 0;
  background: var(--surface);
  border: 1px solid var(--hair, var(--border));
  border-radius: var(--r-sm);
  overflow: hidden;
}
.blank { color: var(--muted); }
.ended { color: var(--muted); font-style: italic; }
/* The edge's act is the kit's ghost icon Button; this keeps it a square at
   the pointer's floor (an icon Button is otherwise 40x44 under a thumb — the
   `button._btn` shape design-system-matrix's coarse arm records as debt). */
.edgeAct { min-inline-size: var(--ctl-h-floor); }
/* It stays INSIDE its cell: a record plate is narrower than the old full-width
   section, and an unconstrained link ran past the cell's clip (rogue census,
   control-clipped). The name yields with an ellipsis; the link keeps its title. */
.rowLink { display: inline-flex; align-items: center; gap: var(--sp-2); color: color-mix(in srgb, var(--brand) 35%, var(--ink)); font-size: var(--fs-sm); font-weight: 600; text-decoration: none;
  min-width: 0; max-width: 100%; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
/* The glyph never yields: only the NAME does. With the link now allowed to
   shrink, a long address squeezed its entity icon to zero width on a phone
   (theme-accessibility relation-phone raster, 2026-09-23). */
.rowLink > :first-child { flex: none; }
.rowLink:hover { text-decoration: underline; }

.cycle, .deep { font-size: var(--fs-xs); color: var(--muted); }

.empty, .none, .err { margin: 0; padding: var(--sp-3) 0; font-size: var(--fs-sm); color: var(--muted); }
.err { color: var(--crit, #c2453d); }
.inline { border: 0; background: none; padding: 0; cursor: pointer; color: var(--brand-text); font: inherit; text-decoration: underline; }
.loading { padding: var(--sp-4) 0; }

.menuHeading {
  padding: var(--sp-2) var(--sp-3); color: var(--muted);
  font-size: var(--fs-xs); font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase;
}
.menuItem { width: 100%; justify-content: flex-start; }
/* The Columns chooser's tick keeps its SPACE when hidden, so the names stay
   aligned whether a column is shown or not. */
.tick { flex: none; }
.tick[data-shown='false'] { visibility: hidden; }

/* High-contrast users still need to see where the relationship grid begins.
   Forced colours replace the brand hue with the user's own system palette;
   depth remains redundantly encoded by nesting/indentation and the trail. */
@media (prefers-contrast: more) {
  .band { border-block-width: 2px; }
  .spine { width: 4px; }
}

@media (forced-colors: active) {
  .band { background: Canvas; border-color: CanvasText; }
  .spine { background: Highlight; forced-color-adjust: none; }
  .grid { border-color: CanvasText; }
}
